import { useCallback } from "react";
import type { ChangeEvent } from "react";
import type { ContentType, Entry } from "@eaub/shared";
import { useApi } from "../../api/ApiProvider";
import { useAsync } from "../../lib/useAsync";
import { asString, asStringArray } from "../../lib/values";
import { Spinner } from "../Spinner";
import { ErrorBanner } from "../ErrorBanner";

interface ReferenceInputProps {
  id: string;
  refTypeId: string;
  multiple: boolean;
  value: unknown;
  onChange: (value: unknown) => void;
}

interface RefData {
  type: ContentType;
  entries: Entry[];
}

function entryTitle(type: ContentType, entry: Entry): string {
  const raw = entry.data[type.titleFieldKey];
  if (typeof raw === "string" && raw.length > 0) return raw;
  return entry.slug;
}

export function ReferenceInput({ id, refTypeId, multiple, value, onChange }: ReferenceInputProps) {
  const api = useApi();
  const load = useCallback(async (): Promise<RefData> => {
    const [type, entries] = await Promise.all([
      api.getContentType(refTypeId),
      api.listEntries(refTypeId),
    ]);
    return { type, entries };
  }, [api, refTypeId]);

  const { data, loading, error, reload } = useAsync<RefData>(load, [load]);

  if (loading) return <Spinner inline label="Loading options…" />;
  if (error) return <ErrorBanner message={error.message} onRetry={reload} />;
  if (!data) return null;

  const { type, entries } = data;

  if (entries.length === 0) {
    return <p className="muted">No “{type.name}” entries exist yet to reference.</p>;
  }

  if (multiple) {
    const selected = asStringArray(value);
    const onSelectMultiple = (event: ChangeEvent<HTMLSelectElement>) => {
      const next = Array.from(event.target.selectedOptions, (opt) => opt.value);
      onChange(next);
    };
    return (
      <select
        id={id}
        className="input"
        multiple
        size={Math.min(6, Math.max(3, entries.length))}
        value={selected}
        onChange={onSelectMultiple}
      >
        {entries.map((entry) => (
          <option key={entry.id} value={entry.id}>
            {entryTitle(type, entry)}
          </option>
        ))}
      </select>
    );
  }

  const selected = asString(value);
  const onSelectSingle = (event: ChangeEvent<HTMLSelectElement>) => {
    const next = event.target.value;
    onChange(next === "" ? undefined : next);
  };
  return (
    <select id={id} className="input" value={selected} onChange={onSelectSingle}>
      <option value="">— none —</option>
      {entries.map((entry) => (
        <option key={entry.id} value={entry.id}>
          {entryTitle(type, entry)}
        </option>
      ))}
    </select>
  );
}
